import { useQuery } from '@tanstack/react-query'
import { Users, Eye, MousePointerClick, TrendingDown, Clock } from 'lucide-react'
import { Header } from '../components/layout/Header'
import { StatsCard } from '../components/charts/StatsCard'
import { VisitorsChart } from '../components/charts/VisitorsChart'
import { DataTable } from '../components/charts/DataTable'
import { RealtimeWidget } from '../components/charts/RealtimeWidget'
import { useSiteStore } from '../stores/siteStore'
import { getFormattedDateRange, getTimezone } from '../stores/analyticsStore'
import { api } from '../lib/api'

export default function Dashboard() {
  const selectedSiteIds = useSiteStore((state) => state.selectedSiteIds)
  const dateRange = getFormattedDateRange()
  const timezone = getTimezone()

  // Fetch overview data
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['overview', selectedSiteIds, dateRange],
    queryFn: async () => {
      if (selectedSiteIds.length === 0) return null
      const response = await api.getOverview({
        siteId: selectedSiteIds.join(','),
        from: dateRange.from,
        to: dateRange.to,
        timezone,
      })
      return response.data as any
    },
    enabled: selectedSiteIds.length > 0,
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Fetch pages data
  const { data: pagesData, isLoading: pagesLoading } = useQuery({
    queryKey: ['pages', selectedSiteIds, dateRange],
    queryFn: async () => {
      if (selectedSiteIds.length === 0) return []
      const response = await api.getPages({
        siteId: selectedSiteIds.join(','),
        from: dateRange.from,
        to: dateRange.to,
        limit: 10,
      })
      return response.data as any[]
    },
    enabled: selectedSiteIds.length > 0,
  })

  // Fetch referrers data
  const { data: referrersData, isLoading: referrersLoading } = useQuery({
    queryKey: ['referrers', selectedSiteIds, dateRange],
    queryFn: async () => {
      if (selectedSiteIds.length === 0) return []
      const response = await api.getReferrers({
        siteId: selectedSiteIds.join(','),
        from: dateRange.from,
        to: dateRange.to,
        limit: 10,
      })
      return response.data as any[]
    },
    enabled: selectedSiteIds.length > 0,
  })

  // Fetch countries data
  const { data: countriesData, isLoading: countriesLoading } = useQuery({
    queryKey: ['countries', selectedSiteIds, dateRange],
    queryFn: async () => {
      if (selectedSiteIds.length === 0) return []
      const response = await api.getCountries({
        siteId: selectedSiteIds.join(','),
        from: dateRange.from,
        to: dateRange.to,
        limit: 10,
      })
      return response.data as any[]
    },
    enabled: selectedSiteIds.length > 0,
  })

  // Fetch devices data
  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ['devices', selectedSiteIds, dateRange],
    queryFn: async () => {
      if (selectedSiteIds.length === 0) return null
      const response = await api.getDevices({
        siteId: selectedSiteIds.join(','),
        from: dateRange.from,
        to: dateRange.to,
      })
      return response.data as any
    },
    enabled: selectedSiteIds.length > 0,
  })

  // Fetch browsers data
  const { data: browsersData, isLoading: browsersLoading } = useQuery({
    queryKey: ['browsers', selectedSiteIds, dateRange],
    queryFn: async () => {
      if (selectedSiteIds.length === 0) return []
      const response = await api.getBrowsers({
        siteId: selectedSiteIds.join(','),
        from: dateRange.from,
        to: dateRange.to,
        limit: 10,
      })
      return response.data as any[]
    },
    enabled: selectedSiteIds.length > 0,
  })

  const formatNumber = (num?: number | null) => {
    const value = typeof num === 'number' && !Number.isNaN(num) ? num : 0
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
    return value.toString()
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.round(seconds % 60)
    return `${minutes}m ${remainingSeconds}s`
  }

  const formatPercentage = (value?: number | null) => {
    const num = typeof value === 'number' && !Number.isNaN(value) ? value : 0
    return `${num.toFixed(1)}%`
  }

  // Convert devices data to table format
  const devicesTableData = devicesData
    ? [
        { device: 'Desktop', count: devicesData.desktop || 0 },
        { device: 'Mobile', count: devicesData.mobile || 0 },
        { device: 'Tablet', count: devicesData.tablet || 0 },
      ]
    : []

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {selectedSiteIds.length === 0 ? (
          <div className="text-center py-16">
            <h2 className="text-2xl font-bold mb-4">No sites selected</h2>
            <p className="text-muted-foreground mb-6">
              Please select at least one site from the dropdown above to view analytics
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Stats Cards Row */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              <StatsCard
                title="Total Pageviews"
                value={formatNumber(overviewData?.pageviews ?? 0)}
                icon={Eye}
                isLoading={overviewLoading}
              />
              <StatsCard
                title="Unique Visitors"
                value={formatNumber(overviewData?.visitors ?? 0)}
                icon={Users}
                isLoading={overviewLoading}
              />
              <StatsCard
                title="Sessions"
                value={formatNumber(overviewData?.sessions ?? 0)}
                icon={MousePointerClick}
                isLoading={overviewLoading}
              />
              <StatsCard
                title="Bounce Rate"
                value={formatPercentage(overviewData?.bounceRate ?? 0)}
                icon={TrendingDown}
                isLoading={overviewLoading}
              />
              <StatsCard
                title="Avg Session Duration"
                value={formatDuration(overviewData?.avgSessionDuration ?? 0)}
                icon={Clock}
                isLoading={overviewLoading}
              />
            </div>

            {/* Real-time Widget */}
            {selectedSiteIds.length === 1 && (
              <RealtimeWidget siteId={selectedSiteIds[0]} />
            )}

            {/* Main Chart */}
            <VisitorsChart
              data={overviewData?.timeseries ?? []}
              domains={overviewData?.domains ?? []}
              isLoading={overviewLoading}
            />

            {/* Data Tables Grid */}
            <div className="grid gap-6 md:grid-cols-2">
              <DataTable
                title="Top Pages"
                data={pagesData ?? []}
                columns={[
                  { key: 'path', label: 'Page' },
                  { key: 'pageviews', label: 'Views', format: formatNumber },
                  { key: 'visitors', label: 'Visitors', format: formatNumber },
                  { key: 'bounceRate', label: 'Bounce Rate', format: formatPercentage },
                ]}
                isLoading={pagesLoading}
              />

              <DataTable
                title="Top Referrers"
                data={referrersData ?? []}
                columns={[
                  { key: 'referrer', label: 'Source' },
                  { key: 'pageviews', label: 'Referrals', format: formatNumber },
                  { key: 'visitors', label: 'Visitors', format: formatNumber },
                ]}
                isLoading={referrersLoading}
              />

              <DataTable
                title="Top Countries"
                data={countriesData ?? []}
                columns={[
                  { key: 'countryName', label: 'Country' },
                  { key: 'visitors', label: 'Visitors', format: formatNumber },
                  { 
                    key: 'visitors', 
                    label: '% of Total',
                    format: (val) => {
                      const total = overviewData?.visitors ?? 1
                      return formatPercentage((Number(val) / total) * 100)
                    }
                  },
                ]}
                isLoading={countriesLoading}
              />

              <DataTable
                title="Devices"
                data={devicesTableData}
                columns={[
                  { key: 'device', label: 'Device' },
                  { key: 'count', label: 'Visitors', format: formatNumber },
                  { 
                    key: 'count', 
                    label: '% of Total',
                    format: (val) => {
                      const total = overviewData?.visitors ?? 1
                      return formatPercentage((Number(val) / total) * 100)
                    }
                  },
                ]}
                isLoading={devicesLoading}
              />
            </div>

            {/* Browsers Table (Full Width) */}
            <DataTable
              title="Browsers"
              data={browsersData ?? []}
              columns={[
                { key: 'browser', label: 'Browser' },
                { key: 'version', label: 'Version' },
                { key: 'visitors', label: 'Visitors', format: formatNumber },
                { key: 'percentage', label: '% of Total', format: formatPercentage },
              ]}
              isLoading={browsersLoading}
            />
          </div>
        )}
      </main>
    </div>
  )
}
